/**
 * Shared editor field components for the mentor self-service panel and the
 * onboarding wizard. Dark premium theme, kebab-case data-testids.
 */
import React from 'react';
import { Plus, Trash2, Check, CalendarDays, Upload, Loader2, Image as ImageIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { uploadFile } from '../../lib/utils';

export const SPECIALTY_OPTIONS = [
  'Kariyer', 'İşletme', 'Yazılım', 'Finans', 'Dijital Pazarlama',
  'Liderlik', 'Girişimcilik', 'Satış', 'UX/UI', 'Veri Bilimi',
  'Finans Danışmanı', 'Pazarlama Danışmanı', 'Satış Koçu', 'Operasyon Danışmanı',
];
export const LANG_OPTIONS = [
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];
export const DAYS = [
  { key: 'mon', label: 'Pzt' }, { key: 'tue', label: 'Sal' }, { key: 'wed', label: 'Çar' },
  { key: 'thu', label: 'Per' }, { key: 'fri', label: 'Cum' }, { key: 'sat', label: 'Cmt' },
  { key: 'sun', label: 'Paz' },
];
export const SLOTS = ['10:00', '12:00', '14:00', '16:00', '19:00', '21:00'];

export const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-slate-500 mt-1">{hint}</span>}
  </label>
);

export const TextInput = ({ value, onChange, placeholder, type = 'text', testid }) => (
  <input
    type={type}
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full h-11 px-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400/50 transition"
    data-testid={testid}
  />
);

export const NumberInput = ({ value, onChange, placeholder, testid }) => (
  <input
    type="number"
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
    placeholder={placeholder}
    className="w-full h-11 px-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400/50 transition"
    data-testid={testid}
  />
);

export const TextArea = ({ value, onChange, placeholder, rows = 4, testid }) => (
  <textarea
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    className="w-full px-3 py-2.5 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400/50 transition resize-y"
    data-testid={testid}
  />
);

export const ChipMultiSelect = ({ options, selected = [], onChange, getKey = (o) => o, getLabel = (o) => o, testidPrefix }) => {
  const toggle = (key) => {
    const has = selected.includes(key);
    onChange(has ? selected.filter((s) => s !== key) : [...selected, key]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const key = getKey(opt);
        const on = selected.includes(key);
        return (
          <button
            key={key}
            type="button"
            onClick={() => toggle(key)}
            className={`inline-flex items-center gap-1 h-9 px-3 rounded-full text-xs border transition ${on ? 'bg-violet-500/20 border-violet-400/50 text-white' : 'bg-white/[0.02] border-white/10 text-slate-300 hover:bg-white/[0.05]'}`}
            data-testid={`${testidPrefix}-${key}`}
          >
            {on && <Check className="w-3 h-3" />} {getLabel(opt)}
          </button>
        );
      })}
    </div>
  );
};

export const ListEditor = ({ items = [], onChange, fields, addLabel, testid }) => {
  const update = (i, key, val) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it));
    onChange(next);
  };
  const add = () => onChange([...items, fields.reduce((a, f) => ({ ...a, [f.key]: '' }), {})]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3" data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2 relative">
          <button type="button" onClick={() => remove(i)} className="absolute top-2 right-2 text-slate-500 hover:text-rose-300 transition" data-testid={`${testid}-remove-${i}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <div className="grid sm:grid-cols-3 gap-2">
            {fields.map((f) => (
              <input
                key={f.key}
                value={it[f.key] ?? ''}
                onChange={(e) => update(i, f.key, e.target.value)}
                placeholder={f.placeholder}
                className="h-10 px-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400/50 transition"
                data-testid={`${testid}-${f.key}-${i}`}
              />
            ))}
          </div>
        </div>
      ))}
      <button type="button" onClick={add} className="inline-flex items-center gap-1.5 text-xs text-violet-300 hover:text-white transition" data-testid={`${testid}-add`}>
        <Plus className="w-3.5 h-3.5" /> {addLabel}
      </button>
    </div>
  );
};

export const PricingEditor = ({ pricing = [], onChange }) => {
  const durations = [30, 60, 90];
  const priceOf = (d) => {
    const p = pricing.find((x) => x.duration_min === d);
    return p ? p.price_try : '';
  };
  const setPrice = (d, val) => {
    const others = pricing.filter((x) => x.duration_min !== d);
    onChange([...others, { duration_min: d, price_try: val === '' ? 0 : Number(val) }].sort((a, b) => a.duration_min - b.duration_min));
  };
  return (
    <div className="grid grid-cols-3 gap-3" data-testid="mentor-pricing-editor">
      {durations.map((d) => (
        <div key={d} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
          <div className="text-xs text-slate-400 mb-2">{d} dakika</div>
          <div className="flex items-center gap-1">
            <span className="text-sm text-slate-400">₺</span>
            <input
              type="number"
              value={priceOf(d)}
              onChange={(e) => setPrice(d, e.target.value)}
              className="w-full h-10 px-2 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white focus:outline-none focus:border-violet-400/50 transition"
              data-testid={`mentor-price-${d}`}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export const AvailabilityEditor = ({ availableHours = {}, onChange }) => {
  const toggle = (day, slot) => {
    const cur = availableHours[day] || [];
    const has = cur.includes(slot);
    onChange({ ...availableHours, [day]: has ? cur.filter((s) => s !== slot) : [...cur, slot].sort() });
  };
  return (
    <div className="space-y-3" data-testid="mentor-availability-editor">
      {DAYS.map((d) => (
        <div key={d.key} className="flex items-center gap-3 flex-wrap">
          <span className="w-10 text-xs text-slate-400 font-medium">{d.label}</span>
          <div className="flex flex-wrap gap-1.5">
            {SLOTS.map((s) => {
              const on = (availableHours[d.key] || []).includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggle(d.key, s)}
                  className={`h-8 px-2.5 rounded-md text-[11px] border transition ${on ? 'bg-violet-500/20 border-violet-400/50 text-white' : 'bg-white/[0.02] border-white/10 text-slate-400 hover:bg-white/[0.05]'}`}
                  data-testid={`mentor-avail-${d.key}-${s.replace(':', '')}`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export const VacationToggle = ({ value = false, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!value)}
    className={`flex items-center justify-between w-full rounded-xl border p-4 transition ${value ? 'border-amber-400/40 bg-amber-500/[0.08]' : 'border-white/10 bg-white/[0.02]'}`}
    data-testid="mentor-vacation-toggle"
  >
    <div className="text-left">
      <div className="text-sm font-medium text-white">Tatil modu</div>
      <div className="text-xs text-slate-400">Açıkken yeni rezervasyon alınmaz.</div>
    </div>
    <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${value ? 'bg-amber-500' : 'bg-white/15'}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${value ? 'translate-x-6' : 'translate-x-1'}`} />
    </span>
  </button>
);

export const BlockedDatesEditor = ({ dates = [], onChange }) => {
  const [open, setOpen] = React.useState(false);
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const pick = (d) => {
    if (!d) return;
    const iso = fmt(d);
    if (!dates.includes(iso)) onChange([...dates, iso].sort());
    setOpen(false);
  };
  return (
    <div data-testid="mentor-blocked-dates">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-2 h-10 px-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-slate-300 hover:border-violet-400/50 transition"
            data-testid="mentor-blocked-date-add"
          >
            <CalendarDays className="w-4 h-4 text-violet-300" /> Kapalı gün ekle
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-[#11111E] border-white/10" align="start">
          <Calendar
            mode="single"
            onSelect={pick}
            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <div className="flex flex-wrap gap-2 mt-3">
        {dates.map((d) => (
          <span key={d} className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full bg-rose-500/10 border border-rose-400/30 text-[11px] text-rose-200" data-testid={`mentor-blocked-${d}`}>
            {d}
            <button type="button" onClick={() => onChange(dates.filter((x) => x !== d))} className="hover:text-white"><Trash2 className="w-3 h-3" /></button>
          </span>
        ))}
        {dates.length === 0 && <span className="text-xs text-slate-500">Kapalı gün yok.</span>}
      </div>
    </div>
  );
};


export const CompletionBar = ({ value = 0 }) => (
  <div data-testid="mentor-completion-bar">
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-xs text-slate-400">Profil tamamlanma</span>
      <span className="text-sm font-semibold text-violet-200 tabular-nums" data-testid="mentor-completion-value">%{value}</span>
    </div>
    <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-violet-500 to-cyan-400 transition-all duration-500"
        style={{ width: `${value}%` }}
      />
    </div>
  </div>
);

// Profile photo upload — real file upload to object storage + URL paste fallback.
export const PhotoUpload = ({ value, onChange, testidPrefix = 'mentor-photo' }) => {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const inputRef = React.useRef(null);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { setErr('JPG, PNG veya WEBP olmalı.'); return; }
    if (file.size > 5 * 1024 * 1024) { setErr('Dosya 5MB sınırını aşıyor.'); return; }
    setErr(null); setBusy(true);
    try {
      const res = await uploadFile('/v2/uploads/photo', file);
      onChange(res.url);
    } catch (e2) {
      setErr(e2?.message || 'Yükleme başarısız');
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-upload`}>
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-full overflow-hidden bg-white/[0.04] border border-white/10 flex items-center justify-center shrink-0">
          {value
            ? <img src={value} alt="" className="w-full h-full object-cover" data-testid={`${testidPrefix}-preview`} />
            : <ImageIcon className="w-6 h-6 text-slate-600" />}
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button" onClick={() => inputRef.current?.click()} disabled={busy}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-violet-500/15 border border-violet-400/30 text-violet-100 text-xs font-medium hover:bg-violet-500/25 disabled:opacity-50 transition"
            data-testid={`${testidPrefix}-btn`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {busy ? 'Yükleniyor…' : 'Fotoğraf yükle'}
          </button>
          <span className="text-[11px] text-slate-500">JPG / PNG / WEBP · max 5MB</span>
        </div>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid={`${testidPrefix}-input`} />
      </div>
      <div>
        <span className="block text-[11px] text-slate-500 mb-1">veya URL yapıştır</span>
        <TextInput value={value} onChange={onChange} placeholder="https://..." testid={`${testidPrefix}-url`} />
      </div>
      {err && <div className="text-xs text-rose-300" data-testid={`${testidPrefix}-error`}>{err}</div>}
    </div>
  );
};

